import React from 'react';
import * as THREE from 'three';
import { useModelStore } from '../../store/useModelStore';
import { useUIStore } from '../../store/useUIStore';
import { useBeamSectionStore } from '../../hooks/useBeamSection';
import { Beam } from '../../types/nnstructural2D';
import { RolledSteelSectionMesh } from './RolledSteelSectionMesh';
import { RolledSteelChannelMesh } from './RolledSteelChannelMesh';
import { RolledSteelEqualAngleMesh } from './RolledSteelEqualAngleMesh';
import { RolledSteelUnequalAngleMesh } from './RolledSteelUnequalAngleMesh';
import { RolledSteelBulbAngleMesh } from './RolledSteelBulbAngleMesh';
import { RolledSteelTeeBarMesh } from './RolledSteelTeeBarMesh';
import { scaleToGrid, getScaleFromRange, getSectionVisualScale } from '@/utils/gridScale';

/**
 * Beam Section Visualization Component
 * 
 * Z-UP COORDINATE SYSTEM (Engineering Standard):
 * - X-axis: Horizontal (East-West)
 * - Y-axis: Horizontal (North-South)  
 * - Z-axis: Vertical (Elevation/Height)
 * 
 * SECTION LOCAL COORDINATE SYSTEM (After Extrusion):
 * - Local X-axis: Cross-section width (horizontal in cross-section plane)
 * - Local Y-axis: Along beam (extrusion/longitudinal direction)
 * - Local Z-axis: Cross-section height (vertical in cross-section plane)
 * 
 * ORIENTATION SYSTEM (Robust & Modular):
 * 1. ALIGNMENT: Quaternion aligns section's Y-axis with beam direction
 * 2. ROTATION: Rotates around beam's longitudinal axis (local Y after alignment)
 * 3. HORIZONTAL FLIP: Mirrors in local XY plane (left-right when looking along beam)
 * 4. VERTICAL FLIP: Mirrors in local YZ plane (up-down when looking along beam)
 * 
 * This approach ensures:
 * - Rotation is always around the beam axis (predictable)
 * - Flips are relative to beam orientation (intuitive)
 * - Works for horizontal, vertical, and diagonal beams
 */

/**
 * Calculate robust section orientation for any beam angle
 * Handles rotation and flips in a modular, predictable way
 */
function calculateSectionOrientation(
  beamDirection: THREE.Vector3,
  rotationDegrees: number,
  flipHorizontal: boolean,
  flipVertical: boolean
): {
  quaternion: THREE.Quaternion;
  rotationQuat: THREE.Quaternion;
  scaleX: number;
  scaleZ: number;
} {
  // Step 1: Create quaternion to align section's Y-axis with beam direction
  const sectionAxis = new THREE.Vector3(0, 1, 0);  // Y-axis: section extrusion
  const alignmentQuat = new THREE.Quaternion();
  alignmentQuat.setFromUnitVectors(sectionAxis, beamDirection);

  // Step 2: Create rotation around beam axis (local Y-axis after alignment)
  const rotationAngle = (rotationDegrees * Math.PI) / 180;
  const rotationQuat = new THREE.Quaternion();
  rotationQuat.setFromAxisAngle(beamDirection, rotationAngle);

  // Step 3: Combine alignment and rotation quaternions
  const finalQuat = new THREE.Quaternion();
  finalQuat.multiplyQuaternions(rotationQuat, alignmentQuat);

  // After alignment: Local X = cross-section width, Local Z = cross-section height
  const scaleX = flipHorizontal ? -1 : 1;  // Left-Right flip
  const scaleZ = flipVertical ? -1 : 1;    // Up-Down flip

  return {
    quaternion: finalQuat,
    rotationQuat,
    scaleX,
    scaleZ
  };
}
export const BeamSectionVisualization: React.FC = () => {
  const { beams, nodes } = useModelStore();
  const sections = useBeamSectionStore((state) => state.sections);
  const isVisible = useBeamSectionStore((state) => state.isVisible);
  const scaleRange = useUIStore((state) => state.scaleRange);

  // Get scaling factor for coordinates
  const scaleFactor = getScaleFromRange(scaleRange);
  
  // Get section visual scale from pre-calculated fixed values
  const sectionScale = getSectionVisualScale(scaleRange);

  // Log whenever sections change for debugging
  React.useEffect(() => {
    const sectionCount = Object.keys(sections).length;
    console.log('🔄 BeamSectionVisualization updated:', {
      sectionCount,
      isVisible,
      scaleFactor,
      sectionScale,
      sections: Object.entries(sections).map(([id, s]) => ({
        id,
        beamIds: s.beamIds,
        type: s.geometry.type
      }))
    });
  }, [sections, isVisible]);

  // Don't render anything if visibility is off
  if (!isVisible) {
    console.log('❌ Sections hidden (isVisible=false)');
    return null;
  }

  const sectionCount = Object.keys(sections).length;
  if (sectionCount === 0) {
    console.log('ℹ️ No sections to render');
    return null;
  }

  console.log(`🏗️ Rendering ${sectionCount} section(s)`);

  return (
    <>
      {Object.values(sections).map((section) => {
        // console.log('🏗️ Processing section:', section.id, 'for beams:', section.beamIds);
        
        // Render section for each beam it's applied to
        return section.beamIds.map((beamId) => {
          const beam = beams.find((b: Beam) => b.id === beamId);
          if (!beam) {
            console.warn('⚠️ Beam not found:', beamId);
            return null;
          }

          const startNode = nodes.find((n) => n.id === beam.startNodeId);
          const endNode = nodes.find((n) => n.id === beam.endNodeId);

          if (!startNode || !endNode) {
            console.warn('⚠️ Nodes not found for beam:', beamId);
            return null;
          }

          const startPos = new THREE.Vector3(
            scaleToGrid(startNode.x, scaleFactor),
            scaleToGrid(startNode.y, scaleFactor),
            scaleToGrid(startNode.z, scaleFactor)
          );
          const endPos = new THREE.Vector3(
            scaleToGrid(endNode.x, scaleFactor),
            scaleToGrid(endNode.y, scaleFactor),
            scaleToGrid(endNode.z, scaleFactor)
          );
          const beamCenter = new THREE.Vector3().lerpVectors(startPos, endPos, 0.5);
          const beamDirection = new THREE.Vector3().subVectors(endPos, startPos).normalize();
          
          // Calculate actual beam length
          const beamLength = startPos.distanceTo(endPos);

          // Get orientation parameters
          const rotationDegrees = section.rotation || 0;
          const flipH = section.flipHorizontal || false;
          const flipV = section.flipVertical || false;

          // console.log('✅ Rendering section at:', beamCenter, 'for beam:', beamId);
          // Calculate robust orientation (quaternion + scales)
          const orientation = calculateSectionOrientation(
            beamDirection,
            rotationDegrees,
            flipH,
            flipV
          );

          console.log('✅ Rendering section:', {
            beamId,
            center: beamCenter,
            beamLength,
            rotation: rotationDegrees,
            flipH,
            flipV,
            beamDir: beamDirection
          });

          return (
            <group
              key={`${section.id}-${beamId}`}
              position={[beamCenter.x, beamCenter.y, beamCenter.z]}
              quaternion={orientation.quaternion}
            >
              {/* Apply flips and sectionScale to cross-section (X,Z), beam length (Y) is already scaled */}
              <group scale={[orientation.scaleX * sectionScale, 1, orientation.scaleZ * sectionScale]}>
              {section.geometry.type === 'cuboid' ? (
                <CuboidSection
                  length={beamLength}
                  breadth={section.geometry.breadth}
                  height={section.geometry.height}
                  color={section.color}
                />
              ) : section.geometry.type === 'cylinder' ? (
                <CylinderSection
                  radius={section.geometry.radius}
                  length={beamLength}
                  color={section.color}
                />
              ) : section.geometry.type === 'rolledSteel' ? (
                <RolledSteelSectionMesh
                  section={{ ...section.geometry, length: beamLength }}
                  color={section.color}
                />
              ) : section.geometry.type === 'rolledChannel' ? (
                <RolledSteelChannelMesh
                  section={{ ...section.geometry, length: beamLength }}
                  color={section.color}
                />
              ) : section.geometry.type === 'rolledEqualAngle' ? (
                <RolledSteelEqualAngleMesh
                  section={{ ...section.geometry, length: beamLength }}
                  color={section.color}
                />
              ) : section.geometry.type === 'rolledUnequalAngle' ? (
                <RolledSteelUnequalAngleMesh
                  section={{ ...section.geometry, length: beamLength }}
                  color={section.color}
                />
              ) : section.geometry.type === 'rolledBulbAngle' ? (
                <RolledSteelBulbAngleMesh
                  section={{ ...section.geometry, length: beamLength }}
                  color={section.color}
                />
              ) : section.geometry.type === 'rolledTeeBar' ? (
                <RolledSteelTeeBarMesh
                  section={{ ...section.geometry, length: beamLength }}
                  color={section.color}
                />
              ) : null}
              </group>
            </group>
          );
        });
      })}
    </>
  );
};

/**
 * Cuboid Section Component
 * IMPORTANT: Length must be along Y-axis to match extrusion pattern
 * Cross-section (breadth × height) is in XZ plane
 */
interface CuboidSectionProps {
  length: number;
  breadth: number;
  height: number;
  color: string;
}

const CuboidSection: React.FC<CuboidSectionProps> = ({
  length,
  breadth,
  height,
  color,
}) => {
  return (
    <mesh>
      {/* [breadth(X), length(Y), height(Z)] - Y-axis aligns with beam after quaternion */}
      <boxGeometry args={[breadth, length, height]} />
      <meshStandardMaterial
        color={color}
        transparent
        opacity={0.40}
        side={THREE.DoubleSide}
      />
      {/* Wireframe overlay for better visualization */}
      <lineSegments>
        <edgesGeometry args={[new THREE.BoxGeometry(breadth, length, height)]} />
        <lineBasicMaterial color={color} linewidth={2} />
      </lineSegments>
    </mesh>
  );
};

/**
 * Cylinder Section Component
 * IMPORTANT: Cylinder's Y-axis aligns with beam direction after quaternion
 * THREE.CylinderGeometry already has Y-axis as its default axis, so this matches perfectly
 */
interface CylinderSectionProps {
  radius: number;
  length: number;
  color: string;
}

const CylinderSection: React.FC<CylinderSectionProps> = ({
  radius,
  length,
  color,
}) => {
  return (
    <mesh>
      {/* CylinderGeometry: Y-axis is default axis (length direction)
          No rotation needed - already matches our Y-axis extrusion pattern */}
      <cylinderGeometry args={[radius, radius, length, 32]} />
      <meshStandardMaterial
        color={color}
        transparent
        opacity={0.4}
        side={THREE.DoubleSide}
      />
      {/* Wireframe overlay */}
      <lineSegments>
        <edgesGeometry args={[new THREE.CylinderGeometry(radius, radius, length, 32)]} />
        <lineBasicMaterial color={color} linewidth={2} />
      </lineSegments>
    </mesh>
  );
};
